import { PROTOCOL_VERSION } from "./limits.ts";
import { CloseCode } from "./limits.ts";

/**
 * Binary frame header (PROTOCOL.md section 4): version `u8`, message type `u16`, little-endian, then the
 * body at offset 3. Every layout module reads and writes the header through these helpers and never
 * throws on hostile bytes (ADR-030).
 */

/**
 * Bytes before the body.
 */
export const HEADER_BYTES = 3;

/**
 * Outcome of decoding a frame: applied, dropped and counted, or the connection closes with a code.
 */
export type DecodeVerdict = { readonly kind: "ok" } | { readonly kind: "drop"; readonly reason: string } | { readonly kind: "close"; readonly code: CloseCode };

/**
 * The verdict for a malformed frame.
 */
export const MALFORMED: DecodeVerdict = { kind: "close", code: CloseCode.Malformed };

/**
 * The verdict for a wrong protocol version.
 */
export const VERSION_MISMATCH: DecodeVerdict = { kind: "close", code: CloseCode.VersionMismatch };

/**
 * The verdict for an applied frame.
 */
export const OK: DecodeVerdict = { kind: "ok" };

/**
 * Writes the header.
 *
 * @param view View over the frame.
 * @param type Message id.
 */
export function writeHeader(view: DataView, type: number): void {
  view.setUint8(0, PROTOCOL_VERSION);
  view.setUint16(1, type, true);
}

/**
 * Writes the header through plain byte stores, for an encoder that runs for every client each tick and so
 * makes no `DataView` (ADR-067: encoding allocates nothing).
 *
 * @param buffer The frame.
 * @param type Message id.
 */
export function putHeader(buffer: Uint8Array, type: number): void {
  buffer[0] = PROTOCOL_VERSION;
  buffer[1] = type & 0xff;
  buffer[2] = type >>> 8;
}

/**
 * Checks the header against an expected type and total length.
 *
 * @param frame The frame bytes.
 * @param type Expected message id.
 * @param length Expected total length in bytes.
 * @returns `OK`, `VERSION_MISMATCH` or `MALFORMED`.
 */
export function checkHeader(frame: Uint8Array, type: number, length: number): DecodeVerdict {
  if (frame.length !== length) {
    return MALFORMED;
  }
  if (frame[0] !== PROTOCOL_VERSION) {
    return VERSION_MISMATCH;
  }
  const actual = (frame[1] as number) | ((frame[2] as number) << 8);
  return actual === type ? OK : MALFORMED;
}

/**
 * The message type a binary frame's header names, read before any layout is checked, so a receiver hands each
 * frame to its own decoder (ADR-074 decision 11).
 *
 * @param frame The frame bytes.
 * @returns The `u16` at offset 1, or -1 for a frame shorter than the header.
 */
export function frameType(frame: Uint8Array): number {
  if (frame.length < HEADER_BYTES) {
    return -1;
  }
  return (frame[1] as number) | ((frame[2] as number) << 8);
}

/**
 * Writes a little-endian `u16` through plain byte stores, for an encoder that makes no `DataView`.
 *
 * @param buffer The frame.
 * @param offset Where.
 * @param value A whole number from 0 to 65,535, checked by the caller.
 */
export function putU16(buffer: Uint8Array, offset: number, value: number): void {
  buffer[offset] = value & 0xff;
  buffer[offset + 1] = value >>> 8;
}

/**
 * Writes a little-endian `u32` through plain byte stores.
 *
 * @param buffer The frame.
 * @param offset Where.
 * @param value A whole number from 0 to 4,294,967,295, checked by the caller.
 */
export function putU32(buffer: Uint8Array, offset: number, value: number): void {
  buffer[offset] = value & 0xff;
  buffer[offset + 1] = (value >>> 8) & 0xff;
  buffer[offset + 2] = (value >>> 16) & 0xff;
  buffer[offset + 3] = value >>> 24;
}

/**
 * Reads a little-endian `u16` from a frame whose length has been checked.
 *
 * @param frame The frame.
 * @param offset Where.
 * @returns The value.
 */
export function readU16(frame: Uint8Array, offset: number): number {
  return (frame[offset] as number) | ((frame[offset + 1] as number) << 8);
}

/**
 * Reads a little-endian `u32` from a frame whose length has been checked.
 *
 * @param frame The frame.
 * @param offset Where.
 * @returns The value.
 */
export function readU32(frame: Uint8Array, offset: number): number {
  return ((frame[offset] as number) | ((frame[offset + 1] as number) << 8) | ((frame[offset + 2] as number) << 16) | ((frame[offset + 3] as number) << 24)) >>> 0;
}

/**
 * Checks that a value fits an unsigned field before an encoder writes it.
 *
 * @param value The value.
 * @param max The largest the field holds.
 * @param message What the error says of the value, such as `act ability 70000`.
 * @throws Error when the value is not a whole number from 0 to `max`.
 */
export function assertField(value: number, max: number, message: string): void {
  if (!Number.isInteger(value) || value < 0 || value > max) {
    throw new Error(`${message} does not fit in 0 to ${max}`);
  }
}

/**
 * A view over a frame's bytes.
 *
 * @param frame The frame.
 * @returns The view.
 */
export function viewOf(frame: Uint8Array): DataView {
  return new DataView(frame.buffer, frame.byteOffset, frame.byteLength);
}
